/**
 * An empty object which can never have additional properties added. Use to avoid allocating new
 * objects in functions which may return an empty object frequently, and where the caller is not
 * likely to need to mutate the returned object. It may also be beneficial to have empty objects
 * be referentially equal to each other (e.g. within react dependency arrays).
 */
const EMPTY_OBJECT = Object.freeze({});
/** Like object spread, but ignores explicit null/undefined in the source object. Allows merging different object types. */
function mergeIgnoringNullish(target, source) {
    if (!source)
        return target;
    const result = { ...target };
    for (const key in source) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        if (source[key] != null)
            result[key] = source[key];
    }
    return result;
}
/**
 * A type-safe version of `Object.keys`. Types the returned keys as the keys of the object.
 */
function objectKeys(obj) {
    return Object.keys(obj);
}
/**
 * A type-safe version of `Object.entries`. Types the returned entries as tuples of key and value.
 */
function objectEntries(obj) {
    return Object.entries(obj);
}
/**
 * Efficiently checks if an object is empty by trying to iterate over it.
 * Faster than checking `Object.keys(obj).length` because it avoids creating an array of keys.
 */
function isEmptyObject(obj) {
    for (const _ in obj)
        return false;
    return true;
}
/**
 * Compare two values one level deep: identical primitives, or objects/arrays whose own enumerable
 * keys all hold `Object.is`-equal values. Anything nested deeper compares by reference.
 *
 * Keys are all that count, so an array and an object holding the same index keys are equal.
 * Mirrors react-redux's `shallowEqual`.
 */
function isShallowEqual(a, b) {
    if (Object.is(a, b))
        return true;
    if (typeof a !== 'object' || a === null)
        return false;
    if (typeof b !== 'object' || b === null)
        return false;
    // At this point, we have two non-null objects
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length)
        return false;
    const aRecord = a;
    const bRecord = b;
    // Matching key counts don't mean matching keys, so `undefined` on one side can't stand in for
    // a key the other side lacks.
    return aKeys.every((key) => Object.prototype.hasOwnProperty.call(bRecord, key) && Object.is(aRecord[key], bRecord[key]));
}
function isNonNullishPrimitive(value) {
    switch (typeof value) {
        case 'string':
        case 'number':
        case 'boolean':
            return true;
    }
    return false;
}

export { EMPTY_OBJECT, isEmptyObject, isNonNullishPrimitive, isShallowEqual, mergeIgnoringNullish, objectEntries, objectKeys };
//# sourceMappingURL=object.js.map
